import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import "../onboarding/loginPage/CreatePasswordPage.css";
import playfulDog from "../../assets/dogLevel3.png";
import apiClient from "../../api/apiClient";
import { useGlobalAlert } from "../../context/AlertContext";
import MobileTopBar from "../../components/MobileTopBar";

const ProfileCreatePasswordPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();
  const location = useLocation();

  const email = location.state?.email || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [requirements, setRequirements] = useState({
    match: false,
    length: false,
    complexity: false,
  });

  const isFormValid = Object.values(requirements).every(Boolean) && email;

  useEffect(() => {
    if (!email) {
      triggerAlert("Email address is missing. Please try again.", "error");
      navigate(-1);
    }
  }, [email, navigate, triggerAlert]);

  useEffect(() => {
    const lengthValid = password.length >= 8 && password.length <= 20;
    const complexityValid = /[\d]|[^A-Za-z0-9]/.test(password);
    const matchValid = password.length > 0 && password === confirmPassword;

    setRequirements({
      length: lengthValid,
      complexity: complexityValid,
      match: matchValid,
    });
  }, [password, confirmPassword]);

  const handleUpdate = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await apiClient.post("/user/change-password", {
        user_email: email,
        new_password: password,
      });

      if (response.data.status) {
        triggerAlert("Password Updated Successfully!", "success");
        navigate("/edit-user-details");
      } else {
        triggerAlert(
          response.data.message ||
            "Failed to update password. Please try again.",
          "error",
        );
      }
    } catch (error) {
      console.error("Password Update Failed", error);
      const errorMessage =
        error.response?.data?.message ||
        "Something went wrong. Please check your connection.";
      triggerAlert(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cp-page-container">
      {/* Header: Back Button */}
      <div className="cp-header desktop-only">
        <button className="cp-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} color="#fff" strokeWidth={3} />
          <span>Back</span>
        </button>
      </div>

      <MobileTopBar
        onBack={() => navigate("/edit-user-details")}
        backColor="#fff"
        logoColor="#fff"
      />
      {/* Main Content */}
      <div className="cp-content-wrapper">
        <div className="cp-mascot-container">
          <img src={playfulDog} alt="Playful Dog" />
        </div>

        <h1 className="cp-title">Change Password?</h1>

        <p className="cp-subtitle">
          Enter a secure password with at least 8 characters,
          <br />
          including one symbol and one number.
        </p>

        <div className="cp-form-area">
          {/* Password Field */}
          <div className="cp-input-group">
            <label>PASSWORD</label>
            <div className="cp-input-wrapper">
              <input
                type={showPass ? "text" : "password"}
                placeholder="Must be min. 8 characters"
                className="cp-input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="cp-eye-btn"
                onClick={() => setShowPass(!showPass)}
              >
                {showPass ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="cp-input-group">
            <label>CONFIRM PASSWORD</label>
            <div className="cp-input-wrapper">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Passwords must match"
                className="cp-input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="cp-eye-btn"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          {/* Validation Checklist */}
          <div className="cp-validation-list">
            <div className={`cp-valid-item ${requirements.match ? "met" : ""}`}>
              <AlertCircle
                size={16}
                fill={requirements.match ? "#47D7AC" : "transparent"}
                stroke={"#009FDC"}
              />
              <span>Passwords must match</span>
            </div>
            <div
              className={`cp-valid-item ${requirements.length ? "met" : ""}`}
            >
              <AlertCircle
                size={16}
                fill={requirements.length ? "#47D7AC" : "transparent"}
                stroke={"#009FDC"}
              />
              <span>Must be 8-20 characters</span>
            </div>
            <div
              className={`cp-valid-item ${requirements.complexity ? "met" : ""}`}
            >
              <AlertCircle
                size={16}
                fill={requirements.complexity ? "#47D7AC" : "transparent"}
                stroke={"#009FDC"}
              />
              <span>
                Must include at least one number or one special character (e.g
                $%&)
              </span>
            </div>
          </div>

          {/* Submit Button - Disabled if not valid */}
          <button
            className={`cp-update-btn ${!isFormValid || isSubmitting ? "disabled" : ""}`}
            onClick={handleUpdate}
            disabled={!isFormValid || isSubmitting}
            style={{
              opacity: isFormValid && !isSubmitting ? 1 : 0.5,
              cursor: isFormValid && !isSubmitting ? "pointer" : "not-allowed",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                CHANGING...
              </>
            ) : (
              "CHANGE PASSWORD"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCreatePasswordPage;
